import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Row, Col, Card, CardBody, Button, Spinner,
} from 'reactstrap';
import { formatFileSize } from 'util/resourceFiles';
import { getSpecialtyGradient } from 'util/specialtyTheme';
import { DEFAULT_SPECIALTY } from 'config/specialtyConfig';

const DeckDownloads = () => {
  const { token } = useParams();
  const [state, setState] = useState({
    loading: true, error: null, firstName: '', specialty: DEFAULT_SPECIALTY, files: [],
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const publicAxios = axios.create();
        const res = await publicAxios.get(`${axios.defaults.baseURL}/leadDeckFiles/${token}`);
        if (!cancelled) {
          setState({
            loading: false,
            error: null,
            firstName: res.data.firstName,
            specialty: res.data.specialty || DEFAULT_SPECIALTY,
            files: res.data.files,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            error: err.response?.data?.error || 'We could not load your downloads.',
            firstName: '',
            specialty: DEFAULT_SPECIALTY,
            files: [],
          });
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div
      className="py-8"
      style={{ background: getSpecialtyGradient(state.specialty), minHeight: '100vh' }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col lg="8">
            <Card className="shadow">
              <CardBody className="p-5">
                {state.loading && (
                  <div className="text-center py-4"><Spinner color="primary" /></div>
                )}

                {!state.loading && state.error && (
                  <div className="text-center py-4">
                    <i className="fas fa-unlink fa-3x text-muted mb-3" />
                    <h3>This link isn't valid</h3>
                    <p className="text-muted">
                      It may have been mistyped or replaced by a newer email.
                    </p>
                    <Link to="/free-anki-deck">
                      <Button color="primary">Request the deck again</Button>
                    </Link>
                  </div>
                )}

                {!state.loading && !state.error && (
                  <>
                    <h2 className="mb-1">
                      {state.firstName ? `${state.firstName}, here are your downloads` : 'Your downloads'}
                    </h2>
                    <p className="text-muted">
                      Each deck can be downloaded once, so save the file somewhere safe.
                    </p>

                    {state.files.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        Nothing is available just yet. We'll email you as soon as it is.
                      </div>
                    ) : (
                      state.files.map((file) => (
                        <div
                          key={file.fileId}
                          className="d-flex align-items-center justify-content-between py-3 border-bottom"
                        >
                          <div className="mr-3">
                            <h5 className="mb-1">{file.title}</h5>
                            {file.description && (
                              <p className="text-muted mb-1"><small>{file.description}</small></p>
                            )}
                            <small className="text-muted">{formatFileSize(file.sizeBytes)}</small>
                          </div>
                          <Button
                            color={file.downloadsRemaining > 0 ? 'primary' : 'secondary'}
                            href={`${axios.defaults.baseURL}/resourceDownload/${token}/${file.fileId}`}
                            disabled={file.downloadsRemaining <= 0}
                          >
                            <i className={`fas ${file.downloadsRemaining > 0 ? 'fa-download' : 'fa-check'} mr-2`} />
                            {file.downloadsRemaining > 0 ? 'Download' : 'Already downloaded'}
                          </Button>
                        </div>
                      ))
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DeckDownloads;
